import { Children, cloneElement, Component, isValidElement } from 'react';
import { Transition, TransitionGroup } from 'react-transition-group';
import { TransitionProps, TransitionStatus } from 'react-transition-group/Transition';
import { ANIMATION_DURATION } from '@optimus-web/common';
import { StyledTransitionGroupProps, TransitionGroupChildBaseProps, TransitionState } from './types';

const reactTransitionStateMap: { [state in TransitionStatus]: TransitionState } = {
    entering: TransitionState.ENTERING,
    entered: TransitionState.ENTERED,
    exiting: TransitionState.EXITING,
    exited: TransitionState.EXITED,
    unmounted: TransitionState.EXITED,
};

export default class StyledTransitionGroup<P extends TransitionGroupChildBaseProps> extends Component<
    StyledTransitionGroupProps<P>
> {
    static defaultProps = {
        childrenProps: {},
        timeoutEnter: 0,
        timeoutExit: ANIMATION_DURATION.default,
    };

    handleReflow = (node: HTMLElement) => {
        // as per https://github.com/reactjs/react-transition-group/blob/7a641148849fc3aa0aa0acb673da9bc3cc35a7a0/src/CSSTransition.js#L190
        // This is for to force a repaint,
        // which is necessary in order to transition styles when adding a class name.
        // eslint-disable-next-line no-unused-expressions
        node.scrollTop;
    };

    handleOnEntering = (node: HTMLElement, isAppearing: boolean) => {
        const { onEntering } = this.props;
        this.handleReflow(node);

        if (onEntering) {
            onEntering(node, isAppearing);
        }
    };

    renderChild = (child: React.ReactNode) => {
        if (!isValidElement<P>(child)) {
            return child;
        }

        const { timeoutEnter, timeoutExit, onEnter, onEntered, onExit, onExiting, onExited } = this.props;
        const transitionProps: TransitionProps = {
            mountOnEnter: true,
            unmountOnExit: true,
            timeout: { enter: timeoutEnter, exit: timeoutExit },
            onEntering: this.handleOnEntering,
            onEnter,
            onEntered,
            onExit,
            onExiting,
            onExited,
        };

        return (
            <Transition {...transitionProps}>
                {(transitionState) =>
                    cloneElement<P>(child, {
                        ...this.props.childrenProps,
                        transitionState: reactTransitionStateMap[transitionState],
                    })
                }
            </Transition>
        );
    };

    render() {
        const {
            // omitted props
            innerRef,
            childrenProps,
            timeoutEnter,
            timeoutExit,
            onEntering,
            onEnter,
            onEntered,
            onExit,
            onExiting,
            onExited,

            children,
            ...other
        } = this.props;

        return <TransitionGroup {...other}>{Children.map(children, this.renderChild)}</TransitionGroup>;
    }
}

// ***THIS SOLUTION IS TEMPORARY AND MUST BE INVESTIGATED***
// Because using StyledTransitionGroup class in styled components causes
// linting errors, you may want to use this wrapper instead in the following way:
// const myComponent = styled(StyledTransitionGroupWrapper)`...`;
// https://mews.myjetbrains.com/youtrack/issue/RND-41125
export const StyledTransitionGroupWrapper = (
    props: Partial<StyledTransitionGroupProps<TransitionGroupChildBaseProps>>
) => <StyledTransitionGroup<TransitionGroupChildBaseProps> {...props} />;
